import React, { useState, useRef, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPrice } from '../../utils/react';
import './MikaExport.scss';

export interface ExportColumn {
    key: string;
    title: string;
    render?: (row: any) => string;
}

interface SumColumnConfig {
    /** Function to extract the numeric value from a row for summing */
    getValue: (row: any) => number;
    /** Label shown before the total (e.g. "Totali:") */
    label: string;
    /** Optional suffix (e.g. "€") */
    suffix?: string;
}

interface MikaExportProps {
    /** Function that fetches ALL records with current filters (perPage=0). Returns array of rows. */
    fetchAllData: () => Promise<any[]>;
    columns: ExportColumn[];
    fileName: string;
    title: string;
    /** If provided, a sum total row will be added at the bottom of the PDF */
    sumColumn?: SumColumnConfig;
    companyProfile?: {
        name?: string;
        address?: string;
        city?: string;
        phone?: string;
        email?: string;
        logo?: string;
        pdfPrimaryColor?: string;
    } | null;
}

const MikaExport: React.FC<MikaExportProps> = ({ fetchAllData, columns, fileName, title, sumColumn, companyProfile }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [open, setOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportType, setExportType] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // The menu is fixed-positioned (see openMenu) so it escapes the toolbar's
    // `overflow-x: auto` clip on mobile. Since fixed coords can't follow the
    // page, close on scroll/resize. Capture phase catches inner-container
    // scrolls too, not just the window.
    useEffect(() => {
        if (!open) return;
        const close = () => setOpen(false);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);

    // Anchor the menu to the button with fixed coordinates, right-aligning its
    // right edge with the button's. Fixed positioning renders it outside the
    // toolbar's overflow box, which was clipping the absolutely-positioned menu.
    const openMenu = () => {
        if (btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setMenuStyle({
                position: 'fixed',
                top: Math.round(r.bottom + 6),
                right: Math.round(Math.max(8, window.innerWidth - r.right)),
            });
        }
        setOpen(true);
    };

    const getRowValues = (row: any): string[] => {
        return columns.map(col => {
            if (col.render) return col.render(row);
            const keys = col.key.split('.');
            let val: any = row;
            for (const k of keys) val = val?.[k];
            return val != null ? String(val) : '';
        });
    };

    const handleExportCSV = async () => {
        setExporting(true);
        setExportType('csv');
        try {
            const allData = await fetchAllData();
            const header = columns.map(c => c.title);
            const rows = allData.map(row =>
                getRowValues(row).map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
            );
            const bom = '\uFEFF';
            const csv = bom + [header.join(','), ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${fileName}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error('CSV export error:', err);
        } finally {
            setExporting(false);
            setExportType(null);
            setOpen(false);
        }
    };

    const handleExportPDF = async () => {
        setExporting(true);
        setExportType('pdf');
        try {
            const allData = await fetchAllData();
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const primaryColor = companyProfile?.pdfPrimaryColor || '#1e3a5f';
            const r = parseInt(primaryColor.slice(1, 3), 16);
            const g = parseInt(primaryColor.slice(3, 5), 16);
            const b = parseInt(primaryColor.slice(5, 7), 16);

            // Header
            let yPos = 15;

            // Company logo — scale to fit within max box while keeping aspect ratio
            let logoW = 0;
            if (companyProfile?.logo) {
                try {
                    const img = new Image();
                    await new Promise<void>((resolve) => {
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                        img.src = companyProfile.logo!;
                    });
                    if (img.naturalWidth && img.naturalHeight) {
                        const maxW = 35;
                        const maxH = 20;
                        const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
                        logoW = img.naturalWidth * ratio;
                        const logoH = img.naturalHeight * ratio;
                        doc.addImage(companyProfile.logo!, 'PNG', 14, 10, logoW, logoH);
                        yPos = 12;
                    }
                } catch (e) {
                    // Logo load failed, continue without it
                }
            }

            // Company info
            const infoX = logoW > 0 ? 14 + logoW + 6 : 14;
            doc.setFontSize(16);
            doc.setTextColor(r, g, b);
            doc.setFont('helvetica', 'bold');
            doc.text(companyProfile?.name || '', infoX, yPos + 2);

            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.setFont('helvetica', 'normal');
            const infoLines: string[] = [];
            if (companyProfile?.address) infoLines.push(companyProfile.address);
            if (companyProfile?.city) infoLines.push(companyProfile.city);
            const contactLine = [companyProfile?.phone, companyProfile?.email].filter(Boolean).join(' · ');
            if (contactLine) infoLines.push(contactLine);
            infoLines.forEach((line, i) => {
                doc.text(line, infoX, yPos + 8 + i * 4);
            });

            // Title + date (right side)
            doc.setFontSize(12);
            doc.setTextColor(r, g, b);
            doc.setFont('helvetica', 'bold');
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.text(title, pageWidth - 14, yPos + 2, { align: 'right' });

            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184);
            doc.setFont('helvetica', 'normal');
            const now = new Date();
            const dateStr = `${fm('common.export.exportedAt', 'Exported:')} ${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
            doc.text(dateStr, pageWidth - 14, yPos + 7, { align: 'right' });
            doc.text(`${allData.length} ${fm('common.export.records', 'records')}`, pageWidth - 14, yPos + 11, { align: 'right' });

            // Sum total in header
            if (sumColumn) {
                const total = allData.reduce((acc, row) => acc + (sumColumn.getValue(row) || 0), 0);
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(r, g, b);
                doc.text(`${sumColumn.label} ${formatPrice(total, sumColumn.suffix || '')}`, pageWidth - 14, yPos + 17, { align: 'right' });
            }

            // Divider line
            const dividerY = Math.max(yPos + (sumColumn ? 28 : 22), 38);
            doc.setDrawColor(r, g, b);
            doc.setLineWidth(0.5);
            doc.line(14, dividerY, pageWidth - 14, dividerY);

            // Table
            const headers = columns.map(c => c.title);
            const body = allData.map(row => getRowValues(row));

            autoTable(doc, {
                head: [headers],
                body: body,
                startY: dividerY + 4,
                margin: { left: 14, right: 14 },
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    lineColor: [226, 232, 240],
                    lineWidth: 0.1,
                    textColor: [51, 65, 85],
                },
                headStyles: {
                    fillColor: [r, g, b],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 8,
                },
                alternateRowStyles: {
                    fillColor: [248, 250, 252],
                },
                didDrawPage: (data: any) => {
                    // Footer on each page
                    const pageHeight = doc.internal.pageSize.getHeight();
                    doc.setFontSize(7);
                    doc.setTextColor(148, 163, 184);
                    doc.text(
                        `${companyProfile?.name || ''} · ${title}`,
                        14,
                        pageHeight - 8
                    );
                    doc.text(
                        `${fm('common.export.page', 'Page')} ${doc.getCurrentPageInfo().pageNumber}`,
                        pageWidth - 14,
                        pageHeight - 8,
                        { align: 'right' }
                    );
                },
            });

            doc.save(`${fileName}.pdf`);
        } catch (err) {
            console.error('PDF export error:', err);
        } finally {
            setExporting(false);
            setExportType(null);
            setOpen(false);
        }
    };

    return (
        <div className="mika-export" ref={menuRef}>
            <button ref={btnRef} className="clp-export-btn" onClick={() => (open ? setOpen(false) : openMenu())} title={fm('common.export.tooltip', 'Export')} type="button" disabled={exporting}>
                {exporting ? (
                    <div className="mika-export-spinner" />
                ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                )}
            </button>
            {open && !exporting && (
                <div className="mika-export-menu" style={menuStyle}>
                    <button className="mika-export-option" onClick={handleExportCSV} type="button">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                        <div className="mika-export-option-text">
                            <span className="mika-export-option-title">
                                <FormattedMessage id="common.export.exportCsv" defaultMessage="Export CSV" />
                            </span>
                            <span className="mika-export-option-desc">Excel, Google Sheets</span>
                        </div>
                    </button>
                    <button className="mika-export-option" onClick={handleExportPDF} type="button">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                        <div className="mika-export-option-text">
                            <span className="mika-export-option-title">
                                <FormattedMessage id="common.export.exportPdf" defaultMessage="Export PDF" />
                            </span>
                            {/*<span className="mika-export-option-desc">Me logo të kompanisë</span>*/}
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default MikaExport;
